import {
  Button,
  Dialog,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { useState } from 'react';
import {
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogPaper,
} from '~/modules/matters/modals/shared';
import { ModalHeader } from '~/shared/components/ModalHeader';
import { useToast } from '~/shared/contexts/ToastContext';
import { useMatterMutations } from '~/modules/matters/useMatterMutations';

const PLACEHOLDER = 'Enter comment here';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
  expertId: string;
  expertName: string;
  isInvited: boolean;
  onRemoved?: () => void;
}

const RemoveExpertModal = ({
  isOpen,
  onClose,
  matterId,
  expertId,
  expertName,
  isInvited,
  onRemoved,
}: Props) => {
  const theme = useTheme();
  const isFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { showToast } = useToast();
  const { removeExpertFromMatter, isRemovingExpert } = useMatterMutations();
  const [comment, setComment] = useState('');

  const handleClose = () => {
    if (isRemovingExpert) {
      return;
    }
    setComment('');
    onClose();
  };

  const handleRemove = async () => {
    const trimmedComment = comment.trim() || undefined;
    try {
      await removeExpertFromMatter({
        matterId,
        expertId,
        exitComment: trimmedComment,
      });
      showToast(
        isInvited
          ? 'Expert removed from the matter. They have been notified.'
          : 'Expert removed from the matter.',
      );
      onRemoved?.();
      handleClose();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'remove_expert_from_matter' },
        extra: { matterId, expertId, isInvited },
      });
      const message =
        error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again.';
      showToast(message, 'error');
    }
  };

  const subtitle = (
    <>
      Remove <strong>{expertName}</strong> from this matter? You can optionally
      add a comment to explain to the expert why you are removing them.
    </>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isFullScreen}
      PaperComponent={StyledDialogPaper}
    >
      <ModalHeader
        title="Remove Expert"
        subtitle={subtitle}
        onClose={handleClose}
      />
      <StyledDialogContent>
        <ContentStack>
          <TextField
            label="Comment (optional)"
            placeholder={PLACEHOLDER}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            multiline
            minRows={3}
            fullWidth
            variant="outlined"
            size="medium"
          />
        </ContentStack>
      </StyledDialogContent>
      <StyledDialogActions>
        <StyledRemoveButton
          onClick={handleRemove}
          color="error"
          variant="contained"
          disabled={isRemovingExpert}
        >
          {isRemovingExpert ? 'Removing…' : 'Remove expert'}
        </StyledRemoveButton>
      </StyledDialogActions>
    </Dialog>
  );
};

const ContentStack = styled(Stack)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  overflow: 'visible',
  gap: theme.spacing(2),
}));

const StyledRemoveButton = styled(Button)(({ theme }) => ({
  minHeight: 44,
  paddingTop: theme.spacing(1.25),
  paddingBottom: theme.spacing(1.25),
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5),
}));

export default RemoveExpertModal;
